import Immutable from 'seamless-immutable';
import download from 'downloadjs';
import Rollbar from 'rollbar';
import { createTypes, completeTypes, completeState, completeReducer, createReducer } from 'redux-recompose';
import { dateToString } from '@widergy/web-utils/lib/date';
import { retrieveErrors } from '@widergy/web-utils/lib/message';
import Polling from '@widergy/polling-handling';
import { push } from 'connected-react-router';
import { downloadPDF } from '@widergy/web-utils/lib/browser';
import { AlertHandler } from '@widergy/energy-ui';

import * as PaymentService from 'services/PaymentService';
import { appConfig } from 'config/customization';
import { message } from 'utils/stringUtils';
import { ROUTES } from 'constants/routes';
import { PAYMENT } from 'constants/survey';

import SurveyActions from './survey/actions';
import { findMessages, mapPaymentFlowError } from './utils';
import { actionCreators as alertActions } from './alertHandlers';
import { AccountUnpaidMapper } from './payments/selectors';

/* ------------- Payment actions ------------- */
export const actions = createTypes(
  completeTypes(
    [
      'GET_PAYMENT_DOCUMENT',
      'GET_PAYMENT_DOCUMENT_PDF',
      'GET_ACCOUNT_UNPAID',
      'GET_USER_UNPAID_BILLS',
      'GET_PAYABLE_DOCUMENTS',
      'GET_MULTIPLE_PAYMENT_DOCUMENTS',
      'GET_PAYMENT_TOKEN',
      'PAYMENT_REQUEST',
      'GET_PAYMENT_IDENTIFIER',
      'SEND_AGENT_TRANSACTION'
    ],
    [
      'RESET_PAYABLE_DOCUMENTS',
      'SET_PAYMENT_METHOD',
      'RESET_PAYMENT_METHOD',
      'PAYMENT_FLOW_INIT',
      'PAYMENT_FLOW_CANCEL',
      'PAYMENT_FLOW_UPDATE_STEP',
      'SET_AGENT_TRANSACTION'
    ]
  ),
  '@@PAYMENT'
);

const privateActionCreators = {
  getPaymentDocumentSuccess: (bills, clientNumber, isPublic) => ({
    type: actions.GET_PAYMENT_DOCUMENT_SUCCESS,
    payload: bills,
    clientNumber,
    isPublic,
    target: 'paymentDocument'
  }),
  getPaymentDocumentFailure: isPublic => ({
    type: actions.GET_PAYMENT_DOCUMENT_FAILURE,
    payload: {},
    isPublic,
    target: 'paymentDocument'
  }),

  getPaymentDocumentPDFSuccess: () => ({
    type: actions.GET_PAYMENT_DOCUMENT_PDF_SUCCESS,
    target: 'paymentDocumentDownloadOnPDF'
  }),

  getPaymentDocumentPDFFailure: error => ({
    type: actions.GET_PAYMENT_DOCUMENT_PDF_FAILURE,
    payload: { error },
    target: 'paymentDocumentDownloadOnPDF'
  }),

  getPayableDocumentsSuccess: documents => ({
    type: actions.GET_PAYABLE_DOCUMENTS_SUCCESS,
    payload: documents,
    target: 'payableDocuments'
  }),
  getPayableDocumentsFailure: error => ({
    type: actions.GET_PAYABLE_DOCUMENTS_FAILURE,
    payload: { error },
    target: 'payableDocuments'
  }),
  getUserUnpaidBillsSuccess: bills => ({
    type: actions.GET_USER_UNPAID_BILLS_SUCCESS,
    payload: bills,
    target: 'userUnpaidBills'
  }),
  getUserUnpaidBillsFailure: error => ({
    type: actions.GET_USER_UNPAID_BILLS_FAILURE,
    payload: { error },
    target: 'userUnpaidBills'
  }),
  getAccountUnpaidSuccess: payload => ({
    type: actions.GET_ACCOUNT_UNPAID_SUCCESS,
    payload,
    target: 'accountUnpaid'
  }),
  getAccountUnpaidFailure: error => ({
    type: actions.GET_ACCOUNT_UNPAID_FAILURE,
    payload: error,
    target: 'accountUnpaid'
  }),
  getMultiplePaymentDocumentsSuccess: paymentDocuments => ({
    type: actions.GET_MULTIPLE_PAYMENT_DOCUMENTS_SUCCESS,
    payload: { paymentDocuments },
    target: 'multiplePaymentDocuments'
  }),
  getMultiplePaymentDocumentsFailure: error => ({
    type: actions.GET_MULTIPLE_PAYMENT_DOCUMENTS_FAILURE,
    payload: { error },
    target: 'multiplePaymentDocuments'
  }),
  getPaymentTokenSuccess: token => ({
    type: actions.GET_PAYMENT_TOKEN_SUCCESS,
    payload: token,
    target: 'paymentToken'
  }),
  getPaymentTokenFailure: error => ({
    type: actions.GET_PAYMENT_TOKEN_FAILURE,
    payload: error,
    target: 'paymentToken'
  }),
  getPaymentIdentifierSuccess: payload => ({
    type: actions.GET_PAYMENT_IDENTIFIER_SUCCESS,
    payload,
    target: 'paymentIdentifier'
  }),
  getPaymentIdentifierFailure: payload => ({
    type: actions.GET_PAYMENT_IDENTIFIER_FAILURE,
    payload,
    target: 'paymentIdentifier'
  }),
  sendAgentTransactionSuccess: () => ({
    type: actions.SEND_AGENT_TRANSACTION_SUCCESS,
    target: 'agentChannelTransaction'
  }),
  sendAgentTransactionFailure: () => ({
    type: actions.SEND_AGENT_TRANSACTION_FAILURE,
    target: 'agentChannelTransaction'
  })
};

export const actionCreators = {
  getPaymentDocument(clientNumber, userData = {}, externalId, isPublic = false) {
    return async dispatch => {
      dispatch({ type: actions.GET_PAYMENT_DOCUMENT, payload: { clientNumber }, target: 'paymentDocument' });
      if (appConfig.encodedDocumentDownload)
        AlertHandler.info(message('Payments:paymentDocDownloadInProgress'));
      const response = await Polling.handle(
        PaymentService.getPrintedPaymentDocument(clientNumber, userData, externalId)
      );
      if (response.error) {
        AlertHandler.error(
          retrieveErrors(response.error, message('DefaultMessages:getPaymentDocumentFailure'))
        );
        dispatch(privateActionCreators.getPaymentDocumentFailure(isPublic));
      } else if (response.data.base64) {
        download(
          `data:application/pdf;base64,${response.data.base64}`,
          message('Payments:paymentDocumentFile', { date: dateToString(new Date()) }),
          'application/pdf'
        );
        dispatch(privateActionCreators.getPaymentDocumentSuccess(null, null, isPublic));
      } else {
        dispatch(privateActionCreators.getPaymentDocumentSuccess(response.data.url, clientNumber, isPublic));
      }
    };
  },

  getPaymentDocumentPDF: (clientNumber, externalId) => async dispatch => {
    dispatch({ type: actions.GET_PAYMENT_DOCUMENT_PDF, target: 'paymentDocumentDownloadOnPDF' });
    const response = await PaymentService.getPrintedPaymentDocumentPDF(clientNumber, externalId);
    if (response.ok) {
      downloadPDF(
        response.data,
        message('Payments:paymentDocumentPDF', { date: dateToString(new Date()) }),
        true
      );
      dispatch(privateActionCreators.getPaymentDocumentPDFSuccess());
    } else {
      dispatch(privateActionCreators.getPaymentDocumentPDFFailure(response.error));
      AlertHandler.error(
        retrieveErrors(response.error, message('DefaultMessages:getPaymentDocumentFailure'))
      );
    }
  },

  getMultiplePaymentDocuments: clientNumber => async dispatch => {
    dispatch({ type: actions.GET_MULTIPLE_PAYMENT_DOCUMENTS, target: 'multiplePaymentDocuments' });
    const response = await PaymentService.getMultiplePaymentDocuments(clientNumber);
    if (response.ok) {
      download(
        response.data,
        message('Payments:multiplePaymentDocumentsZip', { date: dateToString(new Date()) }),
        'application/zip'
      );
      dispatch(privateActionCreators.getMultiplePaymentDocumentsSuccess());
    } else {
      dispatch(privateActionCreators.getMultiplePaymentDocumentsFailure(response.problem));
    }
  },
  getAccountUnpaid: (account, avoidMessage) => async dispatch => {
    dispatch({ type: actions.GET_ACCOUNT_UNPAID, target: 'accountUnpaid' });
    if (!avoidMessage) AlertHandler.info(message('Payments:checkingAccount'));
    const service = account.id
      ? () => PaymentService.getAccountUnpaidBills(account.id)
      : () => PaymentService.getPublicAccountUnpaidBills(account.client_number);
    const response = await Polling.handle(service());
    if (response.error) {
      AlertHandler.error(retrieveErrors(response.error, message('DefaultMessages:getAccountUnpaidFailure')));
      dispatch(privateActionCreators.getAccountUnpaidFailure(response.error));
    } else {
      const unpaidInfo = [AccountUnpaidMapper(response.data, account)];
      dispatch(privateActionCreators.getAccountUnpaidSuccess(unpaidInfo));
    }
  },
  getUserUnpaidBills: accounts => async dispatch => {
    dispatch({ type: actions.GET_USER_UNPAID_BILLS, target: 'userUnpaidBills' });
    let unpaidInfo = [];
    let index = 0;
    accounts.forEach(async account => {
      const response = await Polling.handle(PaymentService.getAccountUnpaidBills(account.id));
      if (response.error) {
        AlertHandler.error(
          retrieveErrors(response.error, message('DefaultMessages:getUserUnpaidBillsFailure'))
        );
        dispatch(privateActionCreators.getUserUnpaidBillsFailure(response.error));
      } else {
        unpaidInfo = [...unpaidInfo, ...AccountUnpaidMapper(response.data, account).debts];
        if (accounts.length === index + 1)
          dispatch(privateActionCreators.getUserUnpaidBillsSuccess(unpaidInfo));
        index += 1;
      }
    });
  },
  getPayableDocuments: (clientNumber, documentType, documentNumber, gender) => async dispatch => {
    dispatch({ type: actions.GET_PAYABLE_DOCUMENTS, target: 'payableDocuments' });
    const response = await Polling.handle(
      PaymentService.getPayableDocuments(clientNumber, documentType, documentNumber, gender)
    );
    if (response.error) {
      AlertHandler.error(
        retrieveErrors(response.error, message('DefaultMessages:getPayableDocumentsFailure'))
      );
      dispatch(privateActionCreators.getPayableDocumentsFailure(response.error));
    } else {
      const data = clientNumber ? [response.data] : response.data;
      const unpaidInfo = data.map(account => AccountUnpaidMapper(account.bills, account));

      const errorMessages = findMessages(data);
      let filteredResult = unpaidInfo.filter(payableAccount =>
        errorMessages.find(
          accountWithError => !accountWithError.client_number === payableAccount.client_number
        )
      );
      errorMessages.forEach(element => {
        AlertHandler.error(element.message);
      });

      filteredResult = errorMessages.length === 0 ? unpaidInfo : filteredResult;

      if (errorMessages.length === 0 || filteredResult.length > 0) {
        dispatch(privateActionCreators.getPayableDocumentsSuccess(unpaidInfo));
      } else {
        dispatch(privateActionCreators.getPayableDocumentsFailure());
      }
    }
  },
  resetPayableDocuments: () => async dispatch => {
    dispatch({ type: actions.RESET_PAYABLE_DOCUMENTS, target: 'payableDocuments' });
  },
  getPaymentToken(bills, values, totalAmount, requestNumber, apikey, url) {
    return async (dispatch, getState) => {
      dispatch({ type: actions.GET_PAYMENT_TOKEN, payload: {}, target: 'paymentToken' });
      const isPublic = !getState().auth.currentUser;
      if (!url || !apikey) {
        AlertHandler.error(message('DefaultMessages:getPaymentTokenFailure'));
        dispatch({
          type: actions.GET_PAYMENT_TOKEN_FAILURE,
          payload: message('DefaultMessages:getPaymentTokenFailure'),
          target: 'paymentToken'
        });
      } else {
        const paymentResponse = await PaymentService.getPaymentToken(values, apikey, url);
        if (paymentResponse.ok) {
          dispatch({
            type: actions.GET_PAYMENT_TOKEN_SUCCESS,
            payload: paymentResponse.data,
            target: 'paymentToken'
          });
          const amount =
            typeof totalAmount === 'string' ? parseFloat(totalAmount).toFixed(2) : totalAmount.toFixed(2);
          await dispatch(
            actionCreators.paymentRequest(
              bills,
              amount,
              paymentResponse.data,
              values.email,
              values.paymentMethod,
              isPublic,
              requestNumber
            )
          );
        } else {
          Rollbar.error('Payment token request error', {
            documentType: values.docType,
            documentNumber: values.docNumber,
            name: values.name,
            cardNumber: values.cardNumber,
            paymentResponse
          });
          const error = retrieveErrors(
            paymentResponse.data,
            message('DefaultMessages:getPaymentTokenFailure')
          );
          dispatch({ type: actions.GET_PAYMENT_TOKEN_FAILURE, payload: error, target: 'paymentToken' });
        }
      }
    };
  },
  setPaymentMethod: paymentMethod => async dispatch => {
    dispatch({ type: actions.SET_PAYMENT_METHOD, payload: paymentMethod, target: 'paymentMethod' });
  },
  resetPaymentMethod: () => async dispatch => {
    dispatch({ type: actions.RESET_PAYMENT_METHOD, target: 'paymentMethod' });
  },
  paymentRequest: (
    bills,
    totalAmount,
    tokenInformation,
    email,
    paymentMethod,
    isPublic,
    requestNumber
  ) => async (dispatch, getState) => {
    dispatch({ type: actions.PAYMENT_REQUEST, target: 'paymentRequest' });
    const response = await Polling.handle(
      PaymentService.paymentRequest(tokenInformation, bills, totalAmount, paymentMethod, email, requestNumber)
    );
    dispatch(actionCreators.resetPaymentMethod());
    if (response.error) {
      const error = retrieveErrors(response.error, message('DefaultMessages:paymentRequestFailure'));
      dispatch(alertActions.openModal({ content: error }));
      dispatch({
        type: actions.PAYMENT_REQUEST_FAILURE,
        payload: error,
        isPublic,
        totalAmount,
        target: 'paymentRequest'
      });
    } else {
      const getSurveyForm = () => dispatch(SurveyActions.getSurveyForm(PAYMENT));
      const { successMessage } = getState().payment.paymentFlow;
      const content = `${response.data.message}<br><br>${successMessage || ''}`;
      dispatch({
        type: actions.PAYMENT_REQUEST_SUCCESS,
        payload: response.data.message,
        isPublic,
        totalAmount,
        target: 'paymentRequest'
      });
      dispatch(alertActions.openModal({ content, onDismiss: appConfig.survey.payment && getSurveyForm }));
    }
  },
  paymentFlowInit: (totalAmount, paymentDetails, procedureId, successMessage) => async (
    dispatch,
    getState
  ) => {
    const step = appConfig.paymentFlow.useCardSelectionStep ? 1 : 1;
    const { max_payment_amount: maxAmountThreshold, max_payment_documents: maxDocsThreshold } =
      getState().utility.appConfiguration || {};
    const error = mapPaymentFlowError(totalAmount, paymentDetails, maxAmountThreshold, maxDocsThreshold);
    if (error) {
      AlertHandler.error(error);
      dispatch(actionCreators.paymentFlowCancel());
    } else {
      dispatch({
        type: actions.PAYMENT_FLOW_INIT,
        payload: {
          totalAmount: totalAmount.toFixed(2),
          paymentDetails,
          procedureId,
          step,
          successMessage
        }
      });
      const isPublic = !getState().auth.currentUser;
      dispatch(push(isPublic ? ROUTES.PUBLIC_PAYMENT_REQUEST : ROUTES.PAYMENT_REQUEST));
    }
  },
  paymentFlowUpdateStep: () => ({
    type: actions.PAYMENT_FLOW_UPDATE_STEP
  }),
  paymentFlowCancel: () => ({
    type: actions.PAYMENT_FLOW_CANCEL
  }),
  getPaymentIdentifier: (gateway, currentAccount, email) => async dispatch => {
    const { client_number: clientNumber } = currentAccount;
    dispatch({ type: actions.GET_PAYMENT_IDENTIFIER, target: 'paymentIdentifier' });
    const response = await Polling.handle(PaymentService.getPaymentIdentifier(gateway, clientNumber, email));
    if (response.error) {
      const error = retrieveErrors(response.error, message('DefaultMessages:getPaymentIdentifierFailure'));
      AlertHandler.error(error);
      dispatch(privateActionCreators.getPaymentIdentifierFailure(error));
    } else {
      dispatch(
        privateActionCreators.getPaymentIdentifierSuccess(response.data && response.data.external_payment_id)
      );
    }
  },
  setAgentTransaction: payload => ({
    type: actions.SET_AGENT_TRANSACTION,
    payload
  }),
  sendAgentTransaction: (type, transactionId, messages) => async dispatch => {
    dispatch({ type: actions.SEND_AGENT_TRANSACTION, target: 'agentChannelTransaction' });
    const response = await PaymentService.sendAgentTransaction(type, transactionId, messages);
    if (response.ok) {
      dispatch(privateActionCreators.sendAgentTransactionSuccess());
    } else {
      dispatch(privateActionCreators.sendAgentTransactionFailure(response.data));
    }
  }
};

/* ------------- Payment reducer ------------- */
export const defaultState = {
  paymentDocument: null,
  payableDocuments: null,
  paymentIdentifier: null,
  userUnpaidBills: [],
  paymentMethod: null,
  paymentFlow: {
    paymentInProgress: false,
    currentStep: 0,
    totalAmount: 0,
    paymentDetails: {
      label: null,
      amount: 0
    },
    procedureId: null,
    successMessage: null
  },
  paymentToken: null,
  accountUnpaid: null,
  agentTransactionId: null
};

const initialState = completeState(defaultState);

const reducerDescription = {
  primaryActions: [
    actions.GET_PAYMENT_DOCUMENT,
    actions.GET_PAYMENT_DOCUMENT_PDF,
    actions.GET_PAYABLE_DOCUMENTS,
    actions.GET_ACCOUNT_UNPAID,
    actions.GET_USER_UNPAID_BILLS,
    actions.GET_MULTIPLE_PAYMENT_DOCUMENTS,
    actions.GET_PAYMENT_TOKEN,
    actions.PAYMENT_REQUEST,
    actions.GET_PAYMENT_IDENTIFIER,
    actions.SEND_AGENT_TRANSACTION
  ],
  override: {
    [actions.RESET_PAYABLE_DOCUMENTS]: (state, action) =>
      Immutable.merge(state, { [action.target]: defaultState[action.target] }),
    [actions.SET_PAYMENT_METHOD]: (state, action) =>
      Immutable.merge(state, { [action.target]: action.payload }),
    [actions.RESET_PAYMENT_METHOD]: (state, action) =>
      Immutable.merge(state, { [action.target]: defaultState[action.target] }),
    [actions.PAYMENT_FLOW_INIT]: (state, action) =>
      Immutable.merge(state, {
        paymentFlow: {
          ...state.paymentFlow,
          paymentInProgress: true,
          totalAmount: action.payload.totalAmount,
          paymentDetails: action.payload.paymentDetails,
          procedureId: action.payload.procedureId,
          currentStep: action.payload.step || 0,
          successMessage: action.payload.successMessage
        },
        paymentIdentifier: defaultState.paymentIdentifier
      }),
    [actions.PAYMENT_FLOW_CANCEL]: state =>
      Immutable.merge(state, {
        paymentFlow: { ...defaultState.paymentFlow, currentStep: state.paymentFlow.currentStep },
        paymentTokenError: null,
        paymentRequest: null,
        paymentRequestError: null
      }),
    [actions.PAYMENT_FLOW_UPDATE_STEP]: state =>
      Immutable.merge(state, {
        paymentFlow: {
          ...state.paymentFlow,
          currentStep: state.paymentFlow.currentStep + 1
        }
      }),
    [actions.GET_ACCOUNT_UNPAID]: state =>
      Immutable.merge(state, {
        accountUnpaidLoading: true,
        accountUnpaid: defaultState.accountUnpaid
      }),
    [actions.SET_AGENT_TRANSACTION]: (state, action) =>
      Immutable.merge(state, {
        agentTransactionId: action.payload
      })
  }
};

export const reducer = createReducer(Immutable(initialState), completeReducer(reducerDescription));
